// @/src/core/store/clientes.store.ts
import type { Cliente, ClientesState } from '@/core/types';
import * as ClientesService from '@/core/services/clientes.service';
import { showToast } from '@/core/utils/ui';
import { toMillisOr, type FirestoreDateLike } from '@shared/fechas/fecha-like';

const initialState: ClientesState = {
  clientes: [],
  usuariosDelSistema: [],
  isLoading: false,
  error: null,
  selectedIds: new Set(),
  isInitialized: false,
};

function createClientesStore() {
    let _userId: string | null = null;
    const listeners: Set<(state: ClientesState) => void> = new Set();

    let state: ClientesState = { ...initialState };

    const notify = () => {
        listeners.forEach(listener => listener(state));
    };

    const subscribe = (listener: (state: ClientesState) => void) => {
        listeners.add(listener);
        listener(state);
        return () => listeners.delete(listener);
    };

    const getState = (): ClientesState => state;

    const update = (updater: (state: ClientesState) => ClientesState) => {
        state = updater(state);
        notify();
    };

    const init = async (userId: string) => {
        // Si ya está inicializado para el mismo usuario, no hacer nada.
        if (state.isInitialized && _userId === userId && !state.error) {
            console.log('[ClientesStore] ✅ Store ya inicializado para este usuario');
            return;
        }

        if (!userId) {
            state = { ...state, error: 'User ID is required', isLoading: false };
            notify();
            return;
        }

        console.log(`[ClientesStore] 🔄 Inicializando store para usuario: ${userId}`);
        _userId = userId;
        state = { ...state, isLoading: true, error: null };
        notify();

        try {
            const [clientesData, usuariosData] = await Promise.all([
                ClientesService.getClientes(userId),
                ClientesService.getSystemUsers()
            ]);

            // Ordenar localmente para asegurar consistencia
            clientesData.sort((a, b) => {
                const timeA = toMillisOr(a.timestampActualizacion as FirestoreDateLike, toMillisOr(a.timestampCreacion as FirestoreDateLike, 0));
                const timeB = toMillisOr(b.timestampActualizacion as FirestoreDateLike, toMillisOr(b.timestampCreacion as FirestoreDateLike, 0));
                return timeB - timeA;
            });

            console.log(`[ClientesStore] ✅ Datos cargados - Clientes: ${clientesData.length}, Usuarios: ${usuariosData.length}`);

            state = {
                ...state,
                clientes: clientesData,
                usuariosDelSistema: usuariosData,
                isLoading: false,
                isInitialized: true,
                error: null,
            };
            notify();

        } catch (error) {
            console.error('[ClientesStore] Error al inicializar:', error);
            state = { ...state, isLoading: false, error: 'Error al cargar los clientes.', isInitialized: false };
            notify();
            showToast('Error al cargar los clientes.', 'error');
        }
    };

    /**
     * Fuerza la recarga de datos desde Firestore.
     * Útil cuando sabemos que los datos han cambiado.
     */
    const reload = async () => {
        if (!_userId) {
            console.warn('[ClientesStore] No se puede recargar sin userId');
            return;
        }

        console.log('[ClientesStore] 🔄 Recargando datos desde Firestore...');
        state = { ...state, isLoading: true, error: null };
        notify();

        try {
            const [clientesData, usuariosData] = await Promise.all([
                ClientesService.getClientes(_userId),
                ClientesService.getSystemUsers()
            ]);

            // Ordenar localmente para asegurar consistencia
            clientesData.sort((a, b) => {
                const timeA = toMillisOr(a.timestampActualizacion as FirestoreDateLike, toMillisOr(a.timestampCreacion as FirestoreDateLike, 0));
                const timeB = toMillisOr(b.timestampActualizacion as FirestoreDateLike, toMillisOr(b.timestampCreacion as FirestoreDateLike, 0));
                return timeB - timeA;
            });

            console.log(`[ClientesStore] ✅ Datos recargados - Clientes: ${clientesData.length}, Usuarios: ${usuariosData.length}`);

            state = {
                ...state,
                clientes: clientesData,
                usuariosDelSistema: usuariosData,
                isLoading: false,
                error: null,
            };
            notify();

        } catch (error) {
            console.error('[ClientesStore] Error al recargar:', error);
            state = { ...state, isLoading: false, error: 'Error al recargar los clientes.' };
            notify();
            showToast('Error al recargar los clientes.', 'error');
        }
    };

    const destroy = () => {
        console.log('[ClientesStore] 🔄 Limpiando listeners del store, pero manteniendo los datos.');
        // _userId = null; // No limpiar userId para poder re-suscribir sin re-loguear
        // state = { ...initialState }; // NO LIMPIAR EL ESTADO
        listeners.clear();
    };

    const reset = () => {
        console.log('[ClientesStore] 🔄 Reseteando store a su estado inicial.');
        _userId = null;
        state = { ...initialState };
        listeners.clear();
        notify(); // Notificar a los suscriptores restantes que el estado se ha limpiado
    };

// --- ACCIONES ASÍNCRONAS ---

async function fetchClientes(filtros: { estado?: string; propietarioId?: string; busqueda?: string; _forceReload?: boolean } = {}) {
    if (!_userId) {
        console.error('[ClientesStore] No se puede buscar clientes: usuario no autenticado');
        update(state => ({ ...state, error: 'Usuario no autenticado.', isLoading: false }));
        return;
    }

    const tieneFiltros = !!(filtros.estado || filtros.propietarioId || filtros.busqueda);
    const necesitaCarga = state.clientes.length === 0 && !state.isLoading;
    const esAccionUsuario = !!filtros._forceReload; // Nueva bandera para acciones del usuario

    console.log('[ClientesStore] fetchClientes called:', {
        filtros,
        tieneFiltros,
        necesitaCarga,
        esAccionUsuario,
        clientesActuales: state.clientes.length,
        isLoading: state.isLoading
    });

    // Ejecutar la consulta si:
    // 1. Se aplicaron filtros explícitamente (incluyendo búsqueda).
    // 2. No hay filtros, pero la lista de clientes está vacía (caso de navegación inicial).
    // 3. Es una acción explícita del usuario (incluye seleccionar "Todos los estados")
    if (tieneFiltros || necesitaCarga || esAccionUsuario) {
        console.log('[ClientesStore] Iniciando consulta a Firestore...');
        update(state => ({ ...state, isLoading: true, error: null }));

        try {
            // La consulta a Firestore se simplifica.
            // Si hay búsqueda, solo se usa ese filtro en la BD.
            // Si no, se usan los demás.
            const consultaParams = {
                busqueda: filtros.busqueda?.toLowerCase(), // Enviar búsqueda en minúsculas
                estado: filtros.busqueda ? undefined : filtros.estado, // No combinar busqueda con otros filtros en la BD
                propietarioId: filtros.busqueda ? undefined : filtros.propietarioId,
                // No incluir _forceReload en los parámetros de Firebase
            };
            
            console.log('[ClientesStore] Parámetros de consulta:', consultaParams);
            let clientes = await ClientesService.getClientes(_userId, consultaParams);

            // Con la nueva indexación de prefijos, el filtrado local post-consulta ya no es necesario
            // para la búsqueda por texto, Firestore ya nos devuelve los resultados relevantes.
            // Solo mantenemos el filtro local para 'estado' y 'propietario' si se usaron junto con una búsqueda,
            // aunque el flujo actual no lo hace (lo cual es más eficiente).
            if (filtros.busqueda && (filtros.estado || filtros.propietarioId)) {
                clientes = clientes.filter(cliente => {
                    const cumpleEstado = !filtros.estado || cliente.estadoCliente === filtros.estado;
                    const cumplePropietario = !filtros.propietarioId || cliente.propietarioId === filtros.propietarioId;
                    return cumpleEstado && cumplePropietario;
                });
            }
            
            // Ordenar localmente para asegurar consistencia en la vista
            clientes.sort((a, b) => {
                const timeA = toMillisOr(a.timestampActualizacion as FirestoreDateLike, toMillisOr(a.timestampCreacion as FirestoreDateLike, 0));
                const timeB = toMillisOr(b.timestampActualizacion as FirestoreDateLike, toMillisOr(b.timestampCreacion as FirestoreDateLike, 0));
                return timeB - timeA;
            });

            console.log(`[ClientesStore] ✅ Consulta completada exitosamente. Encontrados ${clientes.length} clientes`);
            
            // Log de algunos ejemplos de clientes para debugging
            if (clientes.length > 0) {
                console.log('[ClientesStore] Ejemplos de clientes encontrados:', 
                    clientes.slice(0, 3).map(c => ({
                        id: c.id,
                        nombre: c.tipoCliente === 'cliente' 
                            ? `${c.contactos[0]?.nombre || ''} ${c.contactos[0]?.apellido || ''}`.trim()
                            : c.datosEntidad?.nombre,
                        tipo: c.tipoCliente,
                        estado: c.estadoCliente,
                        terminosBusqueda: c.terminosBusqueda?.slice(0, 5) // Solo primeros 5 términos
                    }))
                );
            }

            update(state => ({ ...state, clientes, isLoading: false }));
        } catch (error) {
            console.error('[ClientesStore] ❌ Error al buscar clientes:', error);
            update(state => ({ ...state, error: 'Error al cargar los clientes.', isLoading: false }));
            showToast('Error al cargar los clientes.', 'error');
        }
    } else {
        console.log('[ClientesStore] ⏭️ No se ejecuta consulta: sin filtros y ya hay clientes cargados');
    }
}

async function fetchSystemUsers() {
    // Si ya tenemos usuarios del sistema, no recargar para ahorrar lecturas
    if (state.usuariosDelSistema.length > 0) return;
    
    update(state => ({ ...state, isLoading: true }));
    try {
        const usuarios = await ClientesService.getSystemUsers();
        update(state => ({ ...state, usuariosDelSistema: usuarios, isLoading: false }));
    } catch (error) {
        console.error('Error fetching system users:', error);
        update(state => ({ ...state, error: 'Error al cargar usuarios del sistema.', isLoading: false }));
        showToast('Error al cargar usuarios del sistema.', 'error');
    }
}

async function saveCliente(cliente: Omit<Cliente, 'timestampCreacion' | 'timestampActualizacion'>, esEdicion: boolean) {
    if (!_userId) return showToast('Usuario no autenticado.', 'error');

    update(state => ({ ...state, isLoading: true }));
    try {
        await ClientesService.saveCliente(_userId, cliente, esEdicion);
        showToast(`Cliente ${esEdicion ? 'actualizado' : 'creado'} con éxito.`, 'success');
        
        // Optimización: En lugar de recargar todo, actualizamos el state localmente.
        // La información completa (con timestamps) se recargará la próxima vez que se necesite,
        // o podemos mejorar saveCliente para que devuelva el objeto completo.
        // Por ahora, esta es una mejora significativa.
        const clienteActualizado = {
            ...cliente,
            // Los timestamps son complejos de simular sin la respuesta del servidor,
            // pero para la UI inmediata, esto es suficiente.
            timestampCreacion: esEdicion ? state.clientes.find(c => c.id === cliente.id)?.timestampCreacion : new Date(),
            timestampActualizacion: new Date(), 
            propietarioId: cliente.propietarioId || _userId,
            propietarioNombre: cliente.propietarioNombre || state.usuariosDelSistema.find(u => u.id === (cliente.propietarioId || _userId))?.nombre || 'Yo',
        };

        if (esEdicion) {
            update(state => ({
                ...state,
                clientes: state.clientes.map(c => c.id === cliente.id ? { ...c, ...(clienteActualizado as unknown as Cliente) } : c),
                isLoading: false,
            }));
        } else {
            update(state => ({
                ...state,
                // Forzamos el tipo aquí sabiendo que la recarga traerá los datos completos más adelante
                clientes: [clienteActualizado as unknown as Cliente, ...state.clientes],
                isLoading: false,
            }));
        }
        // await reload(); // Se elimina la recarga completa
    } catch (error) {
        console.error('Error saving cliente:', error);
        update(state => ({ ...state, error: 'Error al guardar el cliente.', isLoading: false }));
        showToast('Error al guardar el cliente.', 'error');
        // Si falla, recargamos para asegurar consistencia
        await reload(); 
    }
}

async function deleteClientes() {
    if (!_userId || state.selectedIds.size === 0) return;

    const clientesParaEliminar = state.clientes
        .filter(c => state.selectedIds.has(c.id))
        .map(c => ({ id: c.id, fotoURL: c.fotoURL }));

    update(state => ({ ...state, isLoading: true }));
    try {
        await ClientesService.deleteClientes(_userId, clientesParaEliminar);
        showToast(`${clientesParaEliminar.length} cliente(s) eliminado(s).`, 'success');
        
        // Optimización: Eliminar del state local en lugar de recargar.
        update(state => ({
            ...state,
            clientes: state.clientes.filter(c => !state.selectedIds.has(c.id)),
            selectedIds: new Set(),
            isLoading: false,
        }));
        // clearSelection(); // Ya se hace arriba
        // await reload(); // Se elimina la recarga completa
    } catch (error) {
        console.error('Error deleting clientes:', error);
        update(state => ({ ...state, error: 'Error al eliminar clientes.', isLoading: false }));
        showToast('Error al eliminar clientes.', 'error');
        // Si falla, recargamos para asegurar consistencia
        await reload();
    }
}

async function cambiarPropietario(nuevoPropietarioId: string, nuevoPropietarioNombre: string) {
    if (!_userId || state.selectedIds.size === 0) return;

    update(state => ({ ...state, isLoading: true }));
    try {
        await ClientesService.cambiarPropietarioClientes(_userId, Array.from(state.selectedIds), nuevoPropietarioId, nuevoPropietarioNombre);
        showToast('Propietario cambiado con éxito.', 'success');
        clearSelection();
        await reload();
    } catch (error)
    {
        console.error('Error cambiando propietario:', error);
        update(state => ({ ...state, error: 'Error al cambiar propietario.', isLoading: false }));
        showToast('Error al cambiar propietario.', 'error');
    }
}

async function cambiarEstado(nuevoEstado: string) {
    if (!_userId || state.selectedIds.size === 0) return;

    update(state => ({ ...state, isLoading: true }));
    try {
        await ClientesService.cambiarEstadoClientes(_userId, Array.from(state.selectedIds), nuevoEstado);
        showToast('Estado cambiado con éxito.', 'success');
        clearSelection();
        await reload();
    } catch (error) {
        console.error('Error cambiando estado:', error);
        update(state => ({ ...state, error: 'Error al cambiar estado.', isLoading: false }));
        showToast('Error al cambiar estado.', 'error');
    }
}

// --- ACCIONES DE SELECCIÓN ---

function toggleClienteSelection(clienteId: string) {
    const currentSelection = new Set(state.selectedIds);
    if (currentSelection.has(clienteId)) {
        currentSelection.delete(clienteId);
    } else {
        currentSelection.add(clienteId);
    }
    update(state => ({ ...state, selectedIds: currentSelection }));
}

function selectAll() {
    const allIds = new Set(state.clientes.map(c => c.id));
    update(state => ({ ...state, selectedIds: allIds }));
}

function clearSelection() {
    update(state => ({ ...state, selectedIds: new Set() }));
}

function toggleSelectAll(checked: boolean) {
    if (checked) {
        selectAll();
    } else {
        clearSelection();
    }
}

    return {
        subscribe,
        getState,
        init,
        reload,
        destroy,
        reset,
        // --- Acciones de datos ---
        fetchClientes,
        fetchSystemUsers,
        saveCliente,
        deleteClientes,
        cambiarPropietario,
        cambiarEstado,
        // --- Acciones de selección ---
        toggleClienteSelection,
        selectAll,
        clearSelection,
        toggleSelectAll,
    };
}

export const useClientesStore = createClientesStore();
